import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { useAgendaAuthorizationQuery } from 'graphql/hooks.generated'
import { TipoAcesso } from 'graphql/types.generated'
import { useCallback, useMemo } from 'react'
import Permissions from 'types/Permissions'

import { LotacaoAgendaModel } from '../model-agenda'

export function useAgendaAuthorization(isAd: boolean = false) {
  const { loading: loadingSessao, data: { profissional: { id: profissionalAcessoId } = {} } = {} } = useSession({
    fetchPolicy: 'cache-first',
  })

  const { hasAuthorization, hasSupervisorAuthorization, acesso } = useAcessoLotacaoOrEstagio()

  const equipeAcessoId = acesso?.equipe?.id

  const {
    loading: loadingEquipes,
    data: { equipe: { emapsApoio: emapsQueApoiamEquipeAcesso, equipesVinculadas } = {} },
  } = useAgendaAuthorizationQuery({
    variables: { equipeId: equipeAcessoId, isAd },
    skip: !equipeAcessoId,
  })

  const loading = loadingSessao || loadingEquipes

  const idsEquipesPodeAlterar = useMemo(
    () =>
      new Set<ID>([
        equipeAcessoId,
        ...(emapsQueApoiamEquipeAcesso?.map((e) => e.id) ?? []),
        ...(equipesVinculadas?.map((e) => e.id) ?? []),
      ]),
    [equipeAcessoId, emapsQueApoiamEquipeAcesso, equipesVinculadas]
  )

  const hasAuthorizationAgendar = useCallback(
    ({ equipe, profissional }: LotacaoAgendaModel) =>
      !loading &&
      hasAuthorization(Permissions.visualizarAgenda.agendarEEditar) &&
      (acesso.tipo !== TipoAcesso.ESTAGIO || hasSupervisorAuthorization(Permissions.visualizarAgenda.agendarEEditar)) &&
      (profissional.id === profissionalAcessoId ||
        !equipe ||
        idsEquipesPodeAlterar.has(equipe.id) ||
        hasAuthorization(Permissions.visualizarAgenda.agendarEEditar.todasAsEquipes)),
    [acesso.tipo, hasAuthorization, hasSupervisorAuthorization, idsEquipesPodeAlterar, loading, profissionalAcessoId]
  )

  const hasAuthorizationAgendarAd = useCallback(
    ({ equipe }: LotacaoAgendaModel) =>
      !loading &&
      (hasAuthorization(
        Permissions.atencaoDomiciliar.visualizarAgendaDeAdDaEquipe.visualizarAgendaDeAdDoMunicipio.agendarEEditar
      ) ||
        (idsEquipesPodeAlterar.has(equipe?.id) &&
          hasAuthorization(Permissions.atencaoDomiciliar.visualizarAgendaDeAdDaEquipe.agendarEEditar))),
    [hasAuthorization, idsEquipesPodeAlterar, loading]
  )

  const hasAuthorizationCancelar = useCallback(
    ({ equipe, profissional }: LotacaoAgendaModel) =>
      !loading &&
      hasAuthorization(Permissions.visualizarAgenda.cancelar) &&
      (profissional.id === profissionalAcessoId ||
        !equipe ||
        equipeAcessoId === equipe.id ||
        hasAuthorization(Permissions.visualizarAgenda.cancelar.todasAsEquipes)),
    [equipeAcessoId, hasAuthorization, loading, profissionalAcessoId]
  )

  const hasAuthorizationCancelarAd = useCallback(
    ({ equipe }: LotacaoAgendaModel) =>
      !loading &&
      (hasAuthorization(
        Permissions.atencaoDomiciliar.visualizarAgendaDeAdDaEquipe.visualizarAgendaDeAdDoMunicipio.cancelar
      ) ||
        (idsEquipesPodeAlterar.has(equipe?.id) &&
          hasAuthorization(Permissions.atencaoDomiciliar.visualizarAgendaDeAdDaEquipe.cancelar))),
    [hasAuthorization, idsEquipesPodeAlterar, loading]
  )

  return useMemo(
    () => ({
      hasAuthorizationAgendar: isAd ? hasAuthorizationAgendarAd : hasAuthorizationAgendar,
      hasAuthorizationCancelar: isAd ? hasAuthorizationCancelarAd : hasAuthorizationCancelar,
    }),
    [hasAuthorizationAgendar, hasAuthorizationAgendarAd, hasAuthorizationCancelar, hasAuthorizationCancelarAd, isAd]
  )
}
